import { Outlet } from 'react-router-dom';
import React, { Suspense, useEffect, useState } from 'react';
const Navbar = React.lazy(() => import('../components/Navbar'));
const Footer = React.lazy(() => import('../components/Footer'));
import { useAuthentication } from '../hooks/useAuthentication';
import { redirectToLogin } from '@/utils/redirect';

import {
  FIND_USER_BY_ID_V5,
  FindUsersPermissionsUserByIdV5Query,
  FindUsersPermissionsUserByIdV5QueryVariables,
} from '@edwin-group/people-graphql';
import { ApolloError, useQuery } from '@apollo/client';
import { loginTracker } from '@/services/auth/auth.service';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export function ProtectedLayout() {
  const { authentication, updateUserContext } = useAuthentication();
  const [alreadyLogged, setAlreadyLogged] = useState(false);

  const userPoolInterval = 60 * 1000 * 5; //5 minutes

  useQuery<FindUsersPermissionsUserByIdV5Query, FindUsersPermissionsUserByIdV5QueryVariables>(FIND_USER_BY_ID_V5, {
    variables: { documentId: authentication?.user?.documentId || '' },
    fetchPolicy: 'network-only',
    pollInterval: userPoolInterval,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => updateUserContext(data),
    onError: handleError,
  });

  useEffect(() => {
    if (!authentication?.user || alreadyLogged) {
      return;
    }

    loginTracker(authentication?.user?.documentId);

    setAlreadyLogged(true);
  }, [authentication?.user, alreadyLogged]);

  function handleError(error: ApolloError) {
    if (
      error?.message === 'UNAUTHENTICATED' ||
      error?.message === 'Forbidden access' ||
      error?.message?.includes('401')
    ) {
      redirectToLogin();
    }
    console.error(error.message);
  }

  if (!authentication?.isAuthenticated) {
    redirectToLogin();
    return null;
  }

  return (
    <Suspense
      fallback={
        <div className="w-full">
          <div className="flex h-screen flex-col items-center justify-center">
            <img src="/loading_logo.png" alt="logo" className="max-h-[146px] max-w-[322px]" />
          </div>
        </div>
      }>
      <div>
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:font-medium focus:outline-none focus:ring-2 focus:ring-primary-600">
          Skip to main content
        </a>
        {authentication?.user.onboardHomeCompleted && <Navbar />}
        <main id="main" className="h-full" tabIndex={-1}>
          <Outlet />
        </main>
        {authentication?.user.onboardHomeCompleted && <Footer />}
      </div>
    </Suspense>
  );
}
